import { useCallback, useEffect, useMemo, useState } from 'react'
import { useFormikContext } from 'formik'
import { useSelector } from 'react-redux'

import { ENTITY_TYPES } from '@sales-center/common'

import { useSearchAssignmentRule } from '@user/components/Settings/LeadAssignmentRule/hooks/useSearchAssignmentRule'
import {
  DEFAULT_GUID_VALUE,
  UNASSIGNED_OWNER_ID,
} from '@user/components/Settings/LeadAssignmentRule/constants'
import { useGetSalesFunnelData } from '@user/components/Settings/SalesFunnels/hooks'
import { selectCurrentUser } from '@user/store/authorization'
import type { ISalesFunnel } from '@user/store/salesFunnel'
import {
  selectAllSalesTeams,
  useGetSalesTeamPermissionMessages,
} from '@user/store/salesTeam'
import type { SalesTeam } from '@user/store/salesTeam'
import { useGetSalesTeamUsers } from '@user/store/salesTeam/hooks'

import type { UpdateRequiredFieldsFormModel } from '../../typings'
import { useMirrorFormValue } from './useMirrorFormValue'

const OVERRIDE_PERMISSIONS = [
  'leads_override_automated_assignment',
  'workwave_marketing_admin',
]

/**
 * Data and handlers for the assignment inputs, ported from Add Lead's Assignment hooks for the flat
 * modal model. Reads the sales teams, funnels and stages from the store (they must be loaded by the
 * caller), applies a matching enabled automated assignment rule (filling team, owner, funnel and
 * status), and tracks whether the user overrode the rule.
 */
export function useAssignmentFields() {
  const { values, setFieldValue, setValues } =
    useFormikContext<UpdateRequiredFieldsFormModel>()
  const { branchId, salesTeamId, assigneeId, salesFunnelId } = values

  const currentUser = useSelector(selectCurrentUser)
  const allSalesTeams = useSelector(selectAllSalesTeams)

  const [isOverriden, setIsOverriden] = useState(false)
  const [ignoreDefault, setIgnoreDefault] = useState(false)

  useMirrorFormValue('stateAbbreviation', 'state')
  const { assignmentRule: rule } = useSearchAssignmentRule(isOverriden)

  const { execute: loadSalesTeamUsers } = useGetSalesTeamUsers()

  // The owner options are built from the team's loaded users, which nothing else in the modal fetches.
  useEffect(() => {
    if (salesTeamId) loadSalesTeamUsers(Number(salesTeamId))
  }, [loadSalesTeamUsers, salesTeamId])

  const canOverrideAutomatedAssignment = useMemo(
    () =>
      OVERRIDE_PERMISSIONS.some((permission) =>
        currentUser.permissions.includes(permission),
      ),
    [currentUser.permissions],
  )

  const { salesFunnels, salesFunnelStages } = useGetSalesFunnelData(
    ENTITY_TYPES.Lead,
    Number(salesFunnelId),
  )

  const salesTeams = useMemo(() => {
    const inBranch = branchId
      ? allSalesTeams.filter((team) => team.branchId === branchId)
      : allSalesTeams
    const limitedByRule =
      !canOverrideAutomatedAssignment && rule?.approvedSalesTeamIds?.length
        ? inBranch.filter((team) => rule.approvedSalesTeamIds.includes(team.id))
        : inBranch
    return [...limitedByRule].sort((a, b) =>
      a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
    )
  }, [allSalesTeams, branchId, canOverrideAutomatedAssignment, rule])

  useEffect(() => {
    if (!rule) return
    const teamId =
      rule.approvedSalesTeamIds?.length === 1
        ? rule.approvedSalesTeamIds[0]
        : null
    let ownerId =
      rule.approvedOwnerIds?.length === 1 ? rule.approvedOwnerIds[0] : ''
    if (ownerId === DEFAULT_GUID_VALUE) ownerId = UNASSIGNED_OWNER_ID

    setValues((v) => ({
      ...v,
      assigneeId: ownerId,
      salesTeamId: teamId,
      salesFunnelId: Number(rule.salesFunnelId) || null,
      salesFunnelStageId: Number(rule.leadStatusId) || null,
    }))
  }, [rule, setValues])

  // SalesFunnelSelect writes the funnel id as a string; the model keeps it numeric.
  useEffect(() => {
    if (typeof salesFunnelId === 'string' && salesFunnelId !== '')
      setFieldValue('salesFunnelId', Number(salesFunnelId), false)
  }, [salesFunnelId, setFieldValue])

  const onChangeOwner = useCallback(
    (value?: unknown) => {
      const ownerId = (value as { id?: string } | undefined)?.id
      if (isOverriden || !ownerId) return
      if (!rule?.approvedOwnerIds?.includes(ownerId)) setIsOverriden(true)
    },
    [isOverriden, rule],
  )

  const onChangeSalesTeam = useCallback(
    async (value: SalesTeam | null) => {
      if (
        !isOverriden &&
        value &&
        rule?.approvedSalesTeamIds?.length &&
        !rule.approvedSalesTeamIds.includes(value.id)
      )
        setIsOverriden(true)
      if (!value) return
      const canOwn =
        (currentUser.permissions.includes('leads_own') &&
          currentUser.salesTeamPositionsData?.salesTeamIds.includes(
            value.id,
          )) ||
        currentUser.permissions.includes('workwave_marketing_admin')
      await setFieldValue('assigneeId', canOwn ? currentUser.userId : '')
      setIgnoreDefault(true)
    },
    [currentUser, isOverriden, rule, setFieldValue],
  )

  const onChangeSalesFunnel = useCallback(
    (value: ISalesFunnel | null) => {
      if (rule?.approvedOwnerIds?.length) setIsOverriden(true)
      setValues((v) => ({
        ...v,
        salesFunnelId: value ? Number(value.id) : null,
        salesFunnelStageId: null,
      }))
    },
    [rule, setValues],
  )

  const onChangeStatus = useCallback(() => {
    if (rule?.approvedOwnerIds?.length) setIsOverriden(true)
  }, [rule])

  const { positionBasedAccessText, selectedSalesTeamAccessText } =
    useGetSalesTeamPermissionMessages(
      'success',
      Number(salesTeamId),
      assigneeId,
    )

  const lockedByRule = Boolean(rule) && !canOverrideAutomatedAssignment
  const salesTeamDisabled =
    lockedByRule && rule?.approvedSalesTeamIds?.length === 1
  const ownerDisabled =
    lockedByRule &&
    rule?.approvedOwnerIds?.length === 1 &&
    assigneeId?.trim() === rule.approvedOwnerIds[0]

  const defaultOwnerId =
    salesTeamId && currentUser.permissions.includes('workwave_marketing_admin')
      ? currentUser.userId
      : undefined

  return {
    rule,
    isOverriden,
    canOverrideAutomatedAssignment,
    ignoreDefault,
    salesTeams,
    salesFunnels,
    salesFunnelStages,
    salesTeamDisabled,
    ownerDisabled,
    lockedByRule,
    defaultOwnerId,
    positionBasedAccessText,
    selectedSalesTeamAccessText,
    onChangeOwner,
    onChangeSalesTeam,
    onChangeSalesFunnel,
    onChangeStatus,
  }
}
